import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { BaseQuestionComponent } from '../../question.component';
import { ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { QuestionBase } from '@jump-tech-frontend/domain';
import { I18nKeys } from '@jump-tech-frontend/app-config';
import { ImageAssetFactory } from '../../../domain/card';
import { ImageAsset, ImageAssetType } from '../../../domain/types';
import { HttpImageComponent } from '../../http-image.component';
import { ImageAssetComponent } from '../../image-asset.component';

import { NgxSpinnerModule } from 'ngx-spinner';
import { QuestionHintComponent, QuestionLabelContentComponent } from '@jump-tech-frontend/question-components';
import { MultiImageAiValidationUploadQuestionInputComponent } from './multi-image-ai-validation-upload-question-input.component';
import { ImageClassificationService } from '../../image-classification.service';
import {
  ClassificationResult,
  ClassificationType,
  ImageWithMetadata,
  normalizeMetadataForStorage
} from '@jump-tech-frontend/domain';
import { firstValueFrom } from 'rxjs';
import { ValidationResultsComponent } from './validation-results/validation-results.component';
import { ValidationResultsPresenter } from './validation-results/validation-results.presenter';
import { BoundingBoxOverlayPresenter } from './bounding-box-overlay/bounding-box-overlay.presenter';
import { BoundingBoxOverlayComponent } from './bounding-box-overlay/bounding-box-overlay.component';

@Component({
  selector: 'crds-question-multi-image-ai-validation-upload',
  imports: [
    ReactiveFormsModule,
    HttpImageComponent,
    ImageAssetComponent,
    MultiImageAiValidationUploadQuestionInputComponent,
    NgxSpinnerModule,
    QuestionHintComponent,
    QuestionLabelContentComponent,
    ValidationResultsComponent,
    BoundingBoxOverlayComponent
  ],
  providers: [ValidationResultsPresenter, BoundingBoxOverlayPresenter],
  templateUrl: './multi-image-ai-validation-upload-question.component.html',
  styleUrls: ['./multi-image-ai-validation-upload-question.component.scss']
})
export class MultiImageAiValidationUploadQuestionComponent extends BaseQuestionComponent implements OnChanges {
  @Input() override form: UntypedFormGroup;
  @Input() override question: QuestionBase<any>;
  @Input() i18ns: I18nKeys;
  @Input() deviceContext?: 'mobile' | 'desktop' = 'desktop';

  images: ImageAsset[];
  uploadedImages: any[] = [];
  uploadedImagesMetaData: Map<number, ClassificationResult> = new Map();
  imageAssetType = ImageAssetType;
  clearInputValue = false;
  showBoundingBoxData = false;
  validationInProgressMap: Map<number, boolean> = new Map();
  showBoundingBoxMap: Map<number, boolean> = new Map();
  readonly imageSkeletonHeight = 450;

  constructor(
    private imageAssetFactory: ImageAssetFactory,
    private imageClassificationService: ImageClassificationService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.question || !Object.prototype.hasOwnProperty.call(changes, 'question')) {
      return;
    }
    if (this.value && !(this.value instanceof Array)) {
      setTimeout(() => {
        this.form?.get(this.question.key)?.patchValue([this.value]);
      });
    }
    this.images = this.imageAssetFactory.create(this.question.image, this.form);
    this.setUploadedImages();
  }

  async onImageUploaded($event: string) {
    if (!this.question.sort) {
      this.question.sort = 'desc';
    }
    const lastUploadedImage = $event;
    if (lastUploadedImage) {
      if (this.question.sort && this.question.sort === 'desc') {
        this.shiftProgressMapIndicesUp();
        this.shiftMapIndicesUp();
        this.shiftBoundingBoxMapIndicesUp();
        this.uploadedImages.unshift(lastUploadedImage);
        this.validationInProgressMap.set(0, true);
        this.form?.get(this.question.key)?.patchValue(this.uploadedImages);
        this.cdr.detectChanges();
      } else {
        this.uploadedImages.push(lastUploadedImage);
        this.validationInProgressMap.set(this.uploadedImages.length - 1, true);
        this.form?.get(this.question.key)?.patchValue(this.uploadedImages);
        this.cdr.detectChanges();
      }

      const result = await this.classifyImage(lastUploadedImage);
      this.showBoundingBoxData = true;

      if (this.question.sort && this.question.sort === 'desc') {
        this.validationInProgressMap.set(0, false);
        this.uploadedImagesMetaData.set(0, result);
        this.showBoundingBoxMap.set(0, true);
      } else {
        const index = this.uploadedImages.length - 1;
        this.validationInProgressMap.set(index, false);
        this.uploadedImagesMetaData.set(index, result);
        this.showBoundingBoxMap.set(index, true);
      }

      this.patchFormWithWrappedImages();
    }
  }

  private async classifyImage(imageBinary: string): Promise<ClassificationResult> {
    try {
      return await firstValueFrom(
        this.imageClassificationService.classifyImage({
          base64Image: imageBinary,
          deviceContext: this.deviceContext,
          classificationType: this.toClassificationType(this.question.imageClassification)
        })
      );
    } catch (error: any) {
      // Handle API errors (500, network errors, etc.) and return error result
      return {
        status: 'error',
        success: false,
        equipmentType: '',
        confidence: 0,
        fuseCutoutConfidence: 0,
        consumerUnitConfidence: 0,
        isolatorSwitchConfidence: 0,
        confidenceThresholdMet: false,
        qualityScore: 0,
        qualityAcceptable: false,
        qualityIssues: [],
        warnings: [],
        error:
          error?.error?.message ||
          error?.message ||
          this.i18ns?.aiValidation?.failedToValidateImage ||
          'Failed to validate image. Please try again.'
      };
    }
  }

  private toClassificationType(value?: string): ClassificationType | undefined {
    switch (value) {
      case 'fuse-cutout':
        return 'fuse_cutout';
      case 'consumer-unit':
        return 'consumer_unit';
      case 'isolator-switch':
        return 'isolator_switch';
      default:
        return undefined;
    }
  }

  get hasUploadedImages() {
    return this.uploadedImages.length > 0;
  }

  /**
   * Get classification result for a given image index
   * @param index - The index of the image in the uploadedImages array
   * @returns The classification result or undefined if not found
   */
  getClassificationResult(index: number): ClassificationResult | undefined {
    return this.uploadedImagesMetaData.get(index);
  }

  /**
   * Check if validation is in progress for a specific image
   * @param index - The index of the image in the uploadedImages array
   * @returns True if validation is in progress for this image
   */
  isValidationInProgress(index: number): boolean {
    return this.validationInProgressMap.get(index) === true;
  }

  /**
   * Toggle bounding box visibility for a specific image
   * @param index - The index of the image in the uploadedImages array
   */
  toggleBoundingBox(index: number): void {
    const currentState = this.showBoundingBoxMap.get(index) ?? true;
    this.showBoundingBoxMap.set(index, !currentState);
  }

  /**
   * Check if bounding boxes are visible for a specific image
   * @param index - The index of the image in the uploadedImages array
   * @returns True if bounding boxes should be shown for this image
   */
  isBoundingBoxVisible(index: number): boolean {
    return this.showBoundingBoxMap.get(index) ?? true; // Default to visible
  }

  /**
   * Get formatted equipment type label from question config
   * Converts snake_case to Title Case for display (e.g., "fuse_cutout" → "Fuse Cutout")
   * Uses translation when available for specific equipment types
   * @returns Formatted equipment type label or "Detection" as fallback
   */
  getEquipmentTypeLabel(): string {
    if (!this.question?.imageClassification) {
      return this.i18ns?.aiValidation?.detection || 'Detection';
    }

    const aiI18n = this.i18ns?.aiValidation;

    // Check for known equipment type translations
    switch (this.question.imageClassification) {
      case 'fuse-cutout':
        return aiI18n?.fuseCutout || 'Fuse Cutout';
      case 'consumer-unit':
        return aiI18n?.consumerUnit || 'Consumer Unit';
      case 'isolator-switch':
        return aiI18n?.isolatorSwitch || 'Isolator Switch';
      default:
        // Convert snake_case to Title Case for unknown types
        return this.question.imageClassification
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
    }
  }

  /**
   * Get translated "Remove" button text
   * @returns Translated remove text or fallback
   */
  getRemoveLabel(): string {
    return this.i18ns?.aiValidation?.remove || 'Remove';
  }

  /**
   * Shift all Map indices up by 1 (when inserting at front with unshift)
   * Moves entries from high to low to avoid overwriting
   */
  private shiftMapIndicesUp(): void {
    const entries = Array.from(this.uploadedImagesMetaData.entries()).sort((a, b) => b[0] - a[0]);
    this.uploadedImagesMetaData.clear();
    entries.forEach(([index, result]) => {
      this.uploadedImagesMetaData.set(index + 1, result);
    });
  }

  /**
   * Shift all progress Map indices up by 1 (when inserting at front with unshift)
   * Moves entries from high to low to avoid overwriting
   */
  private shiftProgressMapIndicesUp(): void {
    const entries = Array.from(this.validationInProgressMap.entries());
    this.validationInProgressMap.clear();
    entries.forEach(([index, inProgress]) => {
      this.validationInProgressMap.set(index + 1, inProgress);
    });
  }

  /**
   * Shift all bounding box visibility Map indices up by 1 (when inserting at front with unshift)
   * Moves entries from high to low to avoid overwriting
   */
  private shiftBoundingBoxMapIndicesUp(): void {
    const entries = Array.from(this.showBoundingBoxMap.entries()).sort((a, b) => b[0] - a[0]);
    this.showBoundingBoxMap.clear();
    entries.forEach(([index, isVisible]) => {
      this.showBoundingBoxMap.set(index + 1, isVisible);
    });
  }

  /**
   * Shift all Map indices down by 1 starting from a given index (when deleting an item)
   * @param fromIndex - Start shifting from this index
   */
  private shiftMapIndicesDown(fromIndex: number): void {
    const maxIndex = Math.max(...Array.from(this.uploadedImagesMetaData.keys()));
    for (let i = fromIndex; i <= maxIndex; i++) {
      const nextResult = this.uploadedImagesMetaData.get(i + 1);
      if (nextResult) {
        this.uploadedImagesMetaData.set(i, nextResult);
      } else {
        this.uploadedImagesMetaData.delete(i);
      }
    }
  }

  /**
   * Shift all progress Map indices down by 1 starting from a given index (when deleting an item)
   * @param fromIndex - Start shifting from this index
   */
  private shiftProgressMapIndicesDown(fromIndex: number): void {
    const maxIndex = Math.max(...Array.from(this.validationInProgressMap.keys()));
    for (let i = fromIndex; i <= maxIndex; i++) {
      const nextInProgress = this.validationInProgressMap.get(i + 1);
      if (nextInProgress !== undefined) {
        this.validationInProgressMap.set(i, nextInProgress);
      } else {
        this.validationInProgressMap.delete(i);
      }
    }
  }

  /**
   * Shift all bounding box visibility Map indices down by 1 starting from a given index (when deleting an item)
   * @param fromIndex - Start shifting from this index
   */
  private shiftBoundingBoxMapIndicesDown(fromIndex: number): void {
    const maxIndex = Math.max(...Array.from(this.showBoundingBoxMap.keys()));
    for (let i = fromIndex; i <= maxIndex; i++) {
      const nextIsVisible = this.showBoundingBoxMap.get(i + 1);
      if (nextIsVisible !== undefined) {
        this.showBoundingBoxMap.set(i, nextIsVisible);
      } else {
        this.showBoundingBoxMap.delete(i);
      }
    }
  }

  /**
   * Wrap uploaded images with their metadata and patch form
   * Creates ImageWithMetadata objects for images that have classification results
   * Metadata is normalized/stringified for form submission to ensure backend and S3 use identical values
   */
  private patchFormWithWrappedImages(): void {
    const wrappedImages = this.uploadedImages.map((img, idx) => {
      const metadata = this.uploadedImagesMetaData.get(idx);
      if (metadata) {
        // Stringify metadata values to match backend serialization format
        // This ensures backend POST and S3 upload use identical string values
        const normalizedMetadata = normalizeMetadataForStorage(metadata);
        return {
          imageData: img,
          metadata: normalizedMetadata
        } as ImageWithMetadata;
      }
      // Return plain string for images without metadata (backward compatibility)
      return img;
    });
    this.form?.get(this.question.key)?.patchValue(wrappedImages);
  }

  /**
   * Get image source for display from form value
   * Extracts imageData from wrapped objects or returns string directly
   * @param index - Index of the image in the form array
   * @returns Image source string for display
   */
  getImageSource(index: number): string {
    const formValue = this.form?.get(this.question.key)?.value;

    if (!formValue || !formValue[index]) {
      return '';
    }

    const value = formValue[index];

    // Handle both wrapped objects and plain strings for backward compatibility
    if (typeof value === 'object') {
      // Object - extract imageData if it exists and is a string
      if (value.imageData && typeof value.imageData === 'string') {
        return value.imageData;
      }
      // Object but no valid imageData - return empty string to prevent [object Object]
      return '';
    }

    // Plain string - return as is
    if (typeof value === 'string') {
      return value;
    }

    // Unexpected type
    return '';
  }

  handleMetadataLoaded(data: ClassificationResult | null | undefined, index: number): void {
    if (data) {
      // Store metadata for this image
      this.uploadedImagesMetaData.set(index, data);

      // Mark validation as complete (no longer in progress)
      this.validationInProgressMap.set(index, false);

      // Show bounding boxes by default when metadata is loaded
      this.showBoundingBoxMap.set(index, true);

      // Trigger change detection so child components (validation-results, bounding-box-overlay) update
      this.cdr.detectChanges();
    } else {
      console.log('No metadata found for image', index);
    }
  }

  clearImage(index: number): void {
    this.uploadedImagesMetaData.delete(index);
    this.shiftMapIndicesDown(index);

    this.validationInProgressMap.delete(index);
    this.shiftProgressMapIndicesDown(index);

    this.showBoundingBoxMap.delete(index);
    this.shiftBoundingBoxMapIndicesDown(index);

    this.uploadedImages.splice(index, 1);

    this.patchFormWithWrappedImages();
    this.cleanInputValue();
  }

  cleanInputValue(): void {
    this.clearInputValue = true;
    setTimeout(() => (this.clearInputValue = false));
  }

  setUploadedImages(): void {
    const values =
      this.form.get(this.question.key) && this.form?.get(this.question.key)?.value
        ? this.form?.get(this.question.key)?.value
        : [];

    // Extract plain image strings from potentially wrapped objects
    this.uploadedImages = values.map((value: ImageWithMetadata | string, index: number) => {
      if (value && typeof value === 'object' && 'imageData' in value) {
        return value.imageData;
      }
      return value;
    });
  }
}
